import type { SystemPermissionKey } from "@nexus/contracts";

import { isBetaFeaturePermissionRoute } from "@/features/auth/lib/page-routes";

// ─── Permission catalog ────────────────────────────────────────────────────────
//
// Single source of truth for the role permission editor (NewRoleDialog,
// TemplateDialog) and the read-only audit view (RoleDrawer). Keep
// routes in sync with SystemPermissions in @nexus/contracts — drift is
// asserted in NewRoleDialog.coverage.test.ts.

export interface PermDef {
  key: SystemPermissionKey;
  type: "PAGE" | "ACTION";
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  route: string;
  name: string;
  description: string;
}

export interface ActionGroup {
  id: string;
  label: string;
  perms: PermDef[];
}

export interface FeatureArea {
  id: string;
  label: string;
  /** Omitted on grouping-only parents (e.g. Beta Features). */
  pagePerm?: PermDef;
  actionGroups?: ActionGroup[];
  /** Individual Beta Features nest here so they are not top-level catalog areas. */
  children?: FeatureArea[];
}

// Derived from SystemPermissions in @nexus/contracts — keep routes in sync.
export const FEATURE_AREAS: FeatureArea[] = [
  {
    id: "identity-hub",
    label: "Identity Hub",
    pagePerm: { key: "IDENTITY_HUB_VIEW_PAGE", type: "PAGE", method: "GET", route: "identity-hub", name: "View Identity Hub", description: "Open the Identity Hub page" },
    actionGroups: [
      {
        id: "ih-people",
        label: "People",
        perms: [
          { key: "IDENTITY_HUB_LIST_USERS",       type: "ACTION", method: "GET",    route: "/users",                name: "List people",       description: "See all users in the organization" },
          { key: "IDENTITY_HUB_LIST_ALL_USERS",   type: "ACTION", method: "GET",    route: "/users/all",            name: "List across orgs",  description: "See users across all organizations" },
          { key: "IDENTITY_HUB_USER_STATS",       type: "ACTION", method: "GET",    route: "/users/stats",          name: "People stats",      description: "Load aggregate counts for the People view" },
          { key: "IDENTITY_HUB_GET_USER",         type: "ACTION", method: "GET",    route: "/users/:userId",        name: "View person",       description: "See a single user's details" },
          { key: "IDENTITY_HUB_GET_USER_ACCESS",  type: "ACTION", method: "GET",    route: "/users/:userId/access", name: "View access",       description: "Inspect a user's assigned roles" },
          { key: "IDENTITY_HUB_LIST_USERS_ACCESS", type: "ACTION", method: "GET",   route: "/users/access/batch",   name: "View access (bulk)", description: "Load assigned roles for the People list in one request" },
          { key: "IDENTITY_HUB_CREATE_USER",      type: "ACTION", method: "POST",   route: "/users/register",       name: "Add person",        description: "Register a new user account" },
          { key: "IDENTITY_HUB_UPDATE_USER",      type: "ACTION", method: "PUT",    route: "/users/update",         name: "Edit person",       description: "Update profile fields" },
          { key: "IDENTITY_HUB_DEACTIVATE_USER",  type: "ACTION", method: "DELETE", route: "/users/deactivate",     name: "Deactivate person", description: "Disable a user without deleting" },
          { key: "IDENTITY_HUB_REACTIVATE_USER",  type: "ACTION", method: "POST",   route: "/users/reactivate",     name: "Reactivate person", description: "Re-enable a deactivated user" },
          { key: "IDENTITY_HUB_GRANT_ACCESS",     type: "ACTION", method: "POST",   route: "/users/access/grant",   name: "Grant role",        description: "Assign a role to a user" },
          { key: "IDENTITY_HUB_REVOKE_ACCESS",    type: "ACTION", method: "POST",   route: "/users/access/revoke",  name: "Revoke role",       description: "Remove a role from a user" },
          { key: "IDENTITY_HUB_BULK_GRANT_ACCESS",  type: "ACTION", method: "POST", route: "/users/access/bulk-grant",  name: "Bulk grant roles",  description: "Assign roles to multiple users at once" },
          { key: "IDENTITY_HUB_BULK_REVOKE_ACCESS", type: "ACTION", method: "POST", route: "/users/access/bulk-revoke", name: "Bulk revoke roles", description: "Remove roles from multiple users at once" },
          { key: "IDENTITY_HUB_REPLACE_ACCESS",     type: "ACTION", method: "POST", route: "/users/access/replace",     name: "Replace roles",     description: "Replace a user's roles in a single operation" },
        ],
      },
      {
        id: "ih-orgs",
        label: "Organizations",
        perms: [
          { key: "IDENTITY_HUB_LIST_TENANTS",       type: "ACTION", method: "GET",    route: "/tenants",              name: "List organizations",      description: "See all organizations" },
          { key: "IDENTITY_HUB_GET_TENANT",         type: "ACTION", method: "GET",    route: "/tenants/:tenantId",    name: "View organization",       description: "See a specific organization's details" },
          { key: "IDENTITY_HUB_CREATE_TENANT",      type: "ACTION", method: "POST",   route: "/tenants/provision",    name: "Create organization",     description: "Provision a new organization" },
          { key: "IDENTITY_HUB_UPDATE_TENANT",      type: "ACTION", method: "PUT",    route: "/tenants/update",       name: "Edit organization",       description: "Update organization settings" },
          { key: "IDENTITY_HUB_DEACTIVATE_TENANT",  type: "ACTION", method: "DELETE", route: "/tenants/deactivate",   name: "Deactivate organization", description: "Disable an organization" },
          { key: "IDENTITY_HUB_REACTIVATE_TENANT", type: "ACTION", method: "POST",   route: "/tenants/reactivate",   name: "Reactivate organization", description: "Re-enable a previously deactivated organization" },
          { key: "IDENTITY_HUB_IMPORT_TENANT_SETTINGS", type: "ACTION", method: "POST", route: "/tenants/import-settings", name: "Import org settings", description: "Copy settings from another organization" },
        ],
      },
      {
        id: "ih-roles",
        label: "Permissions",
        perms: [
          { key: "IDENTITY_HUB_LIST_ACCESS",        type: "ACTION", method: "GET",    route: "/access/by-tenant/:tenantId", name: "List permission sets",       description: "See all permission sets in an organization" },
          { key: "IDENTITY_HUB_LIST_ACCESS_BATCH",  type: "ACTION", method: "GET",    route: "/access/by-tenant/batch",     name: "List permission sets (bulk)", description: "Load permission sets for many orgs in one request (IAM dashboard)" },
          { key: "IDENTITY_HUB_LIST_ALL_ACCESS",    type: "ACTION", method: "GET",    route: "/access/all",                 name: "List permission sets",       description: "List permission sets in the orgs you can access (all orgs for platform admins)" },
          { key: "IDENTITY_HUB_COUNT_ACCESS",       type: "ACTION", method: "GET",    route: "/access/count",               name: "Count permission sets",      description: "Get the permission-set count for the orgs you can access" },
          { key: "IDENTITY_HUB_GET_ACCESS",         type: "ACTION", method: "GET",    route: "/access/:accessId",           name: "View permission set",        description: "Inspect a single permission set" },
          { key: "IDENTITY_HUB_CREATE_ACCESS",      type: "ACTION", method: "POST",   route: "/access/create",              name: "Create permission set",      description: "Create a new permission set" },
          { key: "IDENTITY_HUB_UPDATE_ACCESS",      type: "ACTION", method: "PUT",    route: "/access/update",              name: "Edit permission set",        description: "Rename or update a permission set" },
          { key: "IDENTITY_HUB_DELETE_ACCESS",      type: "ACTION", method: "DELETE", route: "/access/delete",              name: "Delete permission set",      description: "Remove a permission set" },
          { key: "IDENTITY_HUB_RESTORE_ACCESS",    type: "ACTION", method: "POST",   route: "/access/restore",            name: "Restore permission set",     description: "Re-enable a previously deleted permission set" },
          { key: "IDENTITY_HUB_UPDATE_PERMISSIONS", type: "ACTION", method: "PUT",    route: "/access/permissions",         name: "Edit permissions", description: "Change which permissions a permission set has" },
        ],
      },
      {
        id: "ih-templates",
        label: "Roles",
        perms: [
          { key: "IDENTITY_HUB_LIST_TEMPLATES",     type: "ACTION", method: "GET",    route: "/templates",                  name: "View roles",    description: "See the Roles tab and list catalog roles across organizations you can access" },
          { key: "IDENTITY_HUB_GET_TEMPLATE",       type: "ACTION", method: "GET",    route: "/templates/:templateId",      name: "View role",     description: "Inspect a catalog role" },
          { key: "IDENTITY_HUB_CREATE_TEMPLATE",    type: "ACTION", method: "POST",   route: "/templates/create",           name: "Create role",   description: "Create a catalog role" },
          { key: "IDENTITY_HUB_UPDATE_TEMPLATE",    type: "ACTION", method: "PUT",    route: "/templates/update",           name: "Edit role",     description: "Rename or update a catalog role" },
          { key: "IDENTITY_HUB_ARCHIVE_TEMPLATE",   type: "ACTION", method: "POST",   route: "/templates/status",           name: "Archive role",  description: "Archive or restore a catalog role" },
          { key: "IDENTITY_HUB_PREVIEW_TEMPLATE",   type: "ACTION", method: "POST",   route: "/templates/cascade-preview",  name: "Preview role save", description: "See which organizations a save will update before applying" },
          { key: "IDENTITY_HUB_GRANT_TEMPLATE",     type: "ACTION", method: "POST",   route: "/templates/grant",            name: "Grant role",    description: "Grant this catalog role to people (overwrites an existing role in that organization)" },
          { key: "IDENTITY_HUB_REVOKE_TEMPLATE",    type: "ACTION", method: "POST",   route: "/templates/revoke",           name: "Revoke role",   description: "Remove this catalog role from people" },
        ],
      },
      {
        id: "ih-invites",
        label: "Invites",
        perms: [
          { key: "IDENTITY_HUB_LIST_INVITES",      type: "ACTION", method: "GET",    route: "/invites",                  name: "List invites",      description: "See all pending and accepted invitations" },
          { key: "IDENTITY_HUB_GENERATE_INVITE",   type: "ACTION", method: "POST",   route: "/invites",                  name: "Generate invite",   description: "Send invitation links to new users" },
          { key: "IDENTITY_HUB_BULK_INVITE",       type: "ACTION", method: "POST",   route: "/invites/bulk",             name: "Bulk invite",       description: "Invite multiple users at once" },
          { key: "IDENTITY_HUB_REGENERATE_INVITE", type: "ACTION", method: "POST",   route: "/invites/regenerate",       name: "Regenerate invite", description: "Create a fresh invite link for an existing invitation" },
          { key: "IDENTITY_HUB_RESEND_INVITE",     type: "ACTION", method: "POST",   route: "/invites/:inviteId/resend", name: "Resend invite",     description: "Resend an invitation email to the recipient" },
          { key: "IDENTITY_HUB_GET_INVITE_LINK",   type: "ACTION", method: "GET",    route: "/invites/:inviteId/link",   name: "Copy invite link",  description: "Copy an invitation's existing link without regenerating it" },
          { key: "IDENTITY_HUB_REVOKE_INVITE",     type: "ACTION", method: "DELETE", route: "/invites/:inviteId",        name: "Revoke invite",     description: "Cancel an outstanding invitation" },
        ],
      },
      {
        id: "ih-audit",
        label: "Audit logs",
        perms: [
          { key: "IDENTITY_HUB_VIEW_AUDIT_LOG", type: "ACTION", method: "GET", route: "/audit-logs", name: "View audit log", description: "Read who did what across people, orgs, and roles" },
          { key: "IDENTITY_HUB_VIEW_USER_AUDIT_LOG",   type: "ACTION", method: "GET", route: "/audit-logs/users/:userId",     name: "View person audit log", description: "Read the change history for a single person" },
          { key: "IDENTITY_HUB_VIEW_ACCESS_AUDIT_LOG", type: "ACTION", method: "GET", route: "/audit-logs/access/:accessId",   name: "View role audit log",   description: "Read the change history for a single role" },
          { key: "IDENTITY_HUB_VIEW_TENANT_AUDIT_LOG", type: "ACTION", method: "GET", route: "/audit-logs/tenants/:tenantId",  name: "View org audit log",    description: "Read the change history for a single organization" },
        ],
      },
    ],
  },
  {
    id: "admin",
    label: "Admin",
    pagePerm: { key: "ADMIN_VIEW_PAGE", type: "PAGE", method: "GET", route: "admin", name: "View Admin", description: "Open the platform Admin page" },
    actionGroups: [
      {
        id: "admin-events",
        label: "System events",
        perms: [
          { key: "ADMIN_LIST_EVENT_JOBS", type: "ACTION", method: "GET", route: "/admin/events", name: "List event jobs", description: "Inspect this organization's Pub/Sub event job history and status (platform admins see every organization's events)" },
        ],
      },
      {
        id: "admin-changelog-draft",
        label: "Changelog — draft and edit",
        perms: [
          { key: "ADMIN_LIST_CHANGELOG",       type: "ACTION", method: "GET",  route: "/admin/changelog",                            name: "View changelog",       description: "Read the release notes written for each release" },
          { key: "ADMIN_SYNC_CHANGELOG",       type: "ACTION", method: "POST", route: "/admin/changelog/sync",                        name: "Sync releases",        description: "Pull new releases from GitHub and queue notes for them" },
          { key: "ADMIN_REGENERATE_CHANGELOG", type: "ACTION", method: "POST", route: "/admin/changelog/:versionTag/regenerate",      name: "Regenerate notes",     description: "Ask the model to rewrite one release's notes from scratch" },
          { key: "ADMIN_EDIT_CHANGELOG",       type: "ACTION", method: "PUT",  route: "/admin/changelog/:versionTag",                 name: "Edit draft notes",     description: "Rewrite the headline and items before anyone approves them" },
        ],
      },
      {
        id: "admin-changelog-publish",
        label: "Changelog — approve and send",
        perms: [
          { key: "ADMIN_APPROVE_CHANGELOG",         type: "ACTION", method: "POST",   route: "/admin/changelog/:versionTag/approve",       name: "Approve notes",        description: "Mark a draft as ready to go out; blocks further editing" },
          { key: "ADMIN_REVOKE_CHANGELOG_APPROVAL", type: "ACTION", method: "DELETE", route: "/admin/changelog/:versionTag/approval",      name: "Revoke approval",      description: "Reopen approved notes for editing, while nothing has been sent" },
          { key: "ADMIN_SEND_CHANGELOG",            type: "ACTION", method: "POST",   route: "/admin/changelog/:versionTag/send",          name: "Send notes",           description: "Post approved notes to the team's Slack channel — cannot be recalled" },
          { key: "ADMIN_LIST_CHANGELOG_RECIPIENTS", type: "ACTION", method: "GET",    route: "/admin/changelog/recipients",                name: "View recipients",      description: "See which addresses receive changelog email" },
          { key: "ADMIN_ADD_CHANGELOG_RECIPIENT",   type: "ACTION", method: "POST",   route: "/admin/changelog/recipients",                name: "Add recipient",        description: "Add an address to the changelog mailing list" },
          { key: "ADMIN_UPDATE_CHANGELOG_RECIPIENT",type: "ACTION", method: "PATCH",  route: "/admin/changelog/recipients",                name: "Mute recipient",       description: "Switch an address off without losing it from the list" },
          { key: "ADMIN_REMOVE_CHANGELOG_RECIPIENT",type: "ACTION", method: "DELETE", route: "/admin/changelog/recipients/:recipientId",   name: "Remove recipient",     description: "Take an address off the changelog mailing list" },
        ],
      },
      {
        id: "admin-changelog-settings",
        label: "Changelog — settings",
        perms: [
          { key: "ADMIN_GET_CHANGELOG_SETTINGS",    type: "ACTION", method: "GET", route: "/admin/changelog/settings", name: "View release settings", description: "Read how releases are approved, published, and summarised" },
          { key: "ADMIN_UPDATE_CHANGELOG_SETTINGS", type: "ACTION", method: "PUT", route: "/admin/changelog/settings", name: "Change release settings", description: "Decide whether a release reaches Slack without a human, and on what cadence roundups are due" },
          { key: "ADMIN_TEST_CHANGELOG_SLACK",      type: "ACTION", method: "POST", route: "/admin/changelog/slack-test", name: "Send a test Slack message", description: "Post a throwaway message to prove the Slack webhook works" },
          { key: "ADMIN_TEST_ALERTS_SLACK",         type: "ACTION", method: "POST", route: "/admin/changelog/alerts-slack-test", name: "Send a test admin-alerts message", description: "Post a throwaway message to prove the admin-alerts webhook works" },
        ],
      },
      {
        id: "admin-ops-settings",
        label: "Operations — settings",
        perms: [
          { key: "ADMIN_GET_OPS_SETTINGS",    type: "ACTION", method: "GET", route: "/admin/settings/ops", name: "View operations settings", description: "Read how the Events and Scheduled jobs tabs behave — retention, defaults, and alerts" },
          { key: "ADMIN_UPDATE_OPS_SETTINGS", type: "ACTION", method: "PUT", route: "/admin/settings/ops", name: "Change operations settings", description: "Decide how long event history is kept and when a failure streak pages Slack" },
        ],
      },
      {
        id: "admin-changelog-digest-draft",
        label: "Roundups — compose and edit",
        perms: [
          { key: "ADMIN_LIST_CHANGELOG_DIGESTS",   type: "ACTION", method: "GET",    route: "/admin/changelog/digests",                   name: "View roundups",        description: "Read the curated roundups written for the wider company" },
          { key: "ADMIN_LIST_DIGEST_CANDIDATES",   type: "ACTION", method: "GET",    route: "/admin/changelog/digests/candidates",        name: "View what shipped",    description: "See the approved release items not yet in any roundup" },
          { key: "ADMIN_COMPOSE_CHANGELOG_DIGEST", type: "ACTION", method: "POST",   route: "/admin/changelog/digests",                   name: "Compose a roundup",    description: "Pick what is worth telling everyone and have it merged into one message" },
          { key: "ADMIN_REGENERATE_DIGEST",        type: "ACTION", method: "POST",   route: "/admin/changelog/digests/:digestId/regenerate", name: "Merge again",       description: "Ask the model to rewrite a roundup from its picked items" },
          { key: "ADMIN_EDIT_CHANGELOG_DIGEST",    type: "ACTION", method: "PUT",    route: "/admin/changelog/digests/:digestId",         name: "Edit a roundup",       description: "Rewrite a roundup's title, headline, and items before anyone approves it" },
          { key: "ADMIN_DELETE_CHANGELOG_DIGEST",  type: "ACTION", method: "DELETE", route: "/admin/changelog/digests/:digestId",         name: "Discard a roundup",    description: "Throw away an unapproved roundup, returning its releases to the compose list" },
        ],
      },
      {
        id: "admin-changelog-digest-publish",
        label: "Roundups — approve and send",
        perms: [
          { key: "ADMIN_APPROVE_CHANGELOG_DIGEST", type: "ACTION", method: "POST",   route: "/admin/changelog/digests/:digestId/approve", name: "Approve a roundup",    description: "Mark a roundup as ready to go out; blocks further editing" },
          { key: "ADMIN_REVOKE_DIGEST_APPROVAL",   type: "ACTION", method: "DELETE", route: "/admin/changelog/digests/:digestId/approval", name: "Revoke approval",     description: "Reopen an approved roundup for editing, while nothing has been sent" },
          { key: "ADMIN_SEND_CHANGELOG_DIGEST",    type: "ACTION", method: "POST",   route: "/admin/changelog/digests/:digestId/send",    name: "Send a roundup",       description: "Deliver an approved roundup in-app and to the team mailing list — cannot be recalled" },
          { key: "ADMIN_PREVIEW_CHANGELOG_DIGEST_EMAIL", type: "ACTION", method: "GET", route: "/admin/changelog/digests/:digestId/email-preview", name: "Preview a roundup email", description: "See the roundup email before it is sent" },
        ],
      },
    ],
  },
  {
    id: "dashboard",
    label: "Dashboard",
    pagePerm: { key: "DASHBOARD_VIEW_PAGE", type: "PAGE", method: "GET", route: "dashboard", name: "View Dashboard", description: "Open the Dashboard page" },
    actionGroups: [
      {
        id: "dash-data",
        label: "Data",
        perms: [
          { key: "DASHBOARD_INDUSTRIES_LIST",              type: "ACTION", method: "GET", route: "/dashboard/industries",                 name: "List industries",     description: "Load the industry dropdown options" },
          { key: "DASHBOARD_INDUSTRY_PRODUCTS", type: "ACTION", method: "GET", route: "/dashboard/industry-products", name: "Industry products",   description: "Load products for a specific industry" },
        ],
      },
      {
        id: "dash-competitor-defaults",
        label: "Competitor defaults",
        perms: [
          { key: "DASHBOARD_PUT_COMPETITOR_FILTER_DEFAULTS", type: "ACTION", method: "PUT", route: "/dashboard/competitor-filter-defaults", name: "Save competitor defaults", description: "Save the company's competitor filter defaults from Dashboard" },
        ],
      },
    ],
  },
  {
    id: "workspace",
    label: "Workspace",
    pagePerm: { key: "WORKSPACE_VIEW_PAGE", type: "PAGE", method: "GET", route: "workspace", name: "View Workspace", description: "Open the Workspace page" },
    actionGroups: [],
  },
  {
    id: "opportunities",
    label: "Opportunities",
    pagePerm: { key: "OPPORTUNITIES_VIEW_PAGE", type: "PAGE", method: "GET", route: "opportunities", name: "View Opportunities", description: "Open the Opportunities page" },
    actionGroups: [
      {
        id: "opp-browsing",
        label: "Browsing",
        perms: [
          { key: "OPPORTUNITIES_INDUSTRIES_LIST",      type: "ACTION", method: "GET", route: "/opportunities/industries",         name: "List industries",     description: "Load the industry dropdown options" },
          { key: "OPPORTUNITIES_INDUSTRY_PRODUCTS",    type: "ACTION", method: "GET", route: "/opportunities/industry-products",  name: "Industry products",   description: "Load products for a specific industry" },
          { key: "OPPORTUNITIES_TENANT_PRODUCTS",      type: "ACTION", method: "GET", route: "/opportunities/tenant-products",    name: "Tenant products",     description: "Load products linked to the current tenant" },
          { key: "OPPORTUNITIES_TENANT_PRODUCT_TYPES", type: "ACTION", method: "GET", route: "/opportunities/tenant-product-types", name: "Tenant product types", description: "Load product types for Create Set" },
          { key: "OPPORTUNITIES_COMPETITOR_OPTIONS",   type: "ACTION", method: "GET", route: "/opportunities/competitor-options", name: "Competitor options",  description: "Load competitor brand options for a set" },
          { key: "OPPORTUNITIES_LIST",      type: "ACTION", method: "GET", route: "/opportunities/list",     name: "List opportunities",       description: "See all opportunities in a set" },
          { key: "OPPORTUNITIES_FACETS",    type: "ACTION", method: "GET", route: "/opportunities/facets",   name: "List opportunity filters", description: "Load filter options for opportunities" },
          { key: "OPPORTUNITIES_CREATIVES", type: "ACTION", method: "GET", route: "/opportunities/creatives",name: "View creatives",           description: "See creative examples for an opportunity" },
          { key: "OPPORTUNITIES_STATUS",    type: "ACTION", method: "GET", route: "/opportunities/status/:correlationId", name: "View opportunity status", description: "Check the status of a running job" },
        ],
      },
      {
        id: "opp-actions",
        label: "Actions",
        perms: [
          { key: "OPPORTUNITIES_GENERATE_NEW_SET", type: "ACTION", method: "POST", route: "/opportunities/generate-new-set", name: "Generate new opportunity set", description: "Trigger AI generation of a new set" },
          { key: "OPPORTUNITIES_GENERATE",         type: "ACTION", method: "POST", route: "/opportunities/generate",         name: "Generate opportunity",         description: "Run generation for specific opportunities" },
          { key: "OPPORTUNITIES_REJECT",           type: "ACTION", method: "POST", route: "/opportunities/reject",           name: "Reject opportunity",           description: "Bulk-reject opportunities" },
          { key: "OPPORTUNITIES_EXPORT",           type: "ACTION", method: "POST", route: "/opportunities/export",           name: "Export opportunities",         description: "Export opportunities to CSV or slides" },
          { key: "OPPORTUNITIES_CANCEL",           type: "ACTION", method: "POST", route: "/opportunities/cancel",           name: "Cancel generation",            description: "Stop a running opportunity generation job" },
        ],
      },
      {
        id: "opp-sets",
        label: "Sets",
        perms: [
          { key: "OPPORTUNITY_SETS_LIST",   type: "ACTION", method: "GET",    route: "/opportunity-sets/list",       name: "List opportunity sets",   description: "See all opportunity sets" },
          { key: "OPPORTUNITY_SETS_CREATE", type: "ACTION", method: "POST",   route: "/opportunity-sets",            name: "Create opportunity set",  description: "Start a new opportunity set" },
          { key: "OPPORTUNITY_SETS_RENAME", type: "ACTION", method: "PATCH",  route: "/opportunity-sets/:id",        name: "Rename opportunity set",  description: "Rename an existing set" },
          { key: "OPPORTUNITY_SETS_DELETE", type: "ACTION", method: "DELETE", route: "/opportunity-sets/:id",        name: "Delete opportunity set",  description: "Remove an opportunity set" },
          { key: "OPPORTUNITY_SETS_RETRY",  type: "ACTION", method: "POST",   route: "/opportunity-sets/:id/retry",  name: "Retry opportunity set",   description: "Re-run a failed or stalled set" },
          { key: "OPPORTUNITY_SETS_MINIMAL", type: "ACTION", method: "POST", route: "/opportunity-sets/minimal-generation", name: "Minimal opportunity set generation", description: "Create small sets for tests" },
        ],
      },
      {
        id: "opp-scripts",
        label: "Completed Scripts",
        perms: [
          { key: "OPPORTUNITY_SETS_SCRIPTS_LIST",           type: "ACTION", method: "GET",   route: "/opportunity-sets/:id/scripts",                          name: "View scripts",        description: "See completed scripts for a set" },
          { key: "OPPORTUNITY_SETS_SCRIPTS_FACETS",         type: "ACTION", method: "GET",   route: "/opportunity-sets/:id/scripts/facets",                   name: "Script filters",      description: "Load filter options for completed scripts" },
          { key: "OPPORTUNITY_SETS_SCRIPTS_EXPORT",         type: "ACTION", method: "GET",   route: "/opportunity-sets/:id/scripts/export.xlsx",              name: "Export scripts",      description: "Export all completed scripts to Excel" },
          { key: "OPPORTUNITY_SETS_SCRIPTS_EXPORT_BY_IDS",  type: "ACTION", method: "POST",  route: "/opportunity-sets/:id/scripts/export.xlsx",              name: "Export selected scripts", description: "Export chosen scripts to Excel" },
          { key: "OPPORTUNITY_SETS_SCRIPTS_REVERT",         type: "ACTION", method: "PATCH", route: "/opportunity-sets/:id/scripts/:scriptId/revert",         name: "Revert script",       description: "Revert a script to a previous version" },
          { key: "OPPORTUNITY_SETS_SCRIPTS_FAVORITE",       type: "ACTION", method: "PATCH", route: "/opportunity-sets/:id/scripts/:scriptId/favorite",       name: "Favorite script",     description: "Mark a script as a favorite" },
          { key: "OPPORTUNITY_SETS_SCRIPTS_REORDER_SCENES", type: "ACTION", method: "PATCH", route: "/opportunity-sets/:id/scripts/:scriptId/scenes/reorder", name: "Reorder scenes",      description: "Reorder the scenes within a script" },
          { key: "OPPORTUNITY_SETS_SCRIPTS_AI_EDIT",        type: "ACTION", method: "POST",  route: "/opportunity-sets/:id/scripts/ai-edit",                  name: "AI-edit script",      description: "Request an AI edit of a script" },
          { key: "OPPORTUNITY_SETS_SCRIPTS_SUGGEST",        type: "ACTION", method: "POST",  route: "/opportunity-sets/:id/scripts/suggest",                  name: "Suggest scripts",     description: "Request AI script suggestions" },
          { key: "OPPORTUNITY_SETS_SCRIPTS_CUSTOM_BRIEF",   type: "ACTION", method: "POST",  route: "/opportunity-sets/:id/scripts/custom-brief",             name: "Create custom brief", description: "Upload a custom script brief for an opportunity set" },
          { key: "OPPORTUNITY_SETS_SCRIPTS_CUSTOM_ASSET_DOWNLOAD", type: "ACTION", method: "GET", route: "/opportunity-sets/:id/scripts/:scriptId/custom-asset/download", name: "Download custom asset", description: "Download a custom script asset from an opportunity set" },
          { key: "OPPORTUNITY_SETS_SCRIPTS_RESOLVE_LINKS",  type: "ACTION", method: "POST",  route: "/opportunity-sets/:id/scripts/resolve-links",            name: "Resolve reference links", description: "Fetch and validate reference links before script generation" },
          { key: "OPPORTUNITIES_SCRIPT_STORYBOARD_GENERATE", type: "ACTION", method: "POST", route: "/opportunity-sets/:id/scripts/:scriptId/storyboard",     name: "Generate storyboard", description: "Generate a storyboard image for a script" },
        ],
      },
    ],
  },
  {
    id: "persona-opportunities",
    label: "Opportunity Hub",
    pagePerm: {
      key: "PERSONA_OPPORTUNITIES_VIEW_PAGE",
      type: "PAGE",
      method: "GET",
      route: "persona-opportunities",
      name: "View Opportunity Hub",
      description: "Open the Opportunity Hub page",
    },
    actionGroups: [
      {
        id: "persona-opportunity-sets",
        label: "Opportunity sets",
        perms: [
          {
            key: "PERSONA_OPPORTUNITY_SETS_LIST",
            type: "ACTION",
            method: "GET",
            route: "/persona-opportunity-sets",
            name: "View opportunity sets",
            description: "List opportunity sets and open a set's results",
          },
          {
            key: "PERSONA_OPPORTUNITY_SETS_GENERATE",
            type: "ACTION",
            method: "POST",
            route: "/persona-opportunity-sets/generate",
            name: "Create opportunity sets",
            description: "Generate a new opportunity set",
          },
          {
            key: "PERSONA_OPPORTUNITY_SETS_QUERY_TUNING",
            type: "ACTION",
            method: "POST",
            route: "/persona-opportunity-sets/generate/query-tuning",
            name: "Query tuning on generate",
            description: "Override BQ query tuning knobs on Opportunity Hub generate",
          },
          {
            key: "PERSONA_OPPORTUNITY_SETS_REFERENCE_VIDEO_PRESIGN",
            type: "ACTION",
            method: "POST",
            route: "/uploads/presign",
            name: "Upload script reference video",
            description: "Presign a structural-reference mp4 for persona script generation",
          },
          {
            key: "PERSONA_OPPORTUNITY_SETS_REFERENCE_VIDEO_CONFIRM",
            type: "ACTION",
            method: "POST",
            route: "/uploads/confirm",
            name: "Confirm script reference video",
            description: "Confirm a structural-reference mp4 upload",
          },
          {
            key: "PERSONA_OPPORTUNITY_SETS_GUIDELINES_EXTRACT",
            type: "ACTION",
            method: "POST",
            route: "/persona-opportunity-sets/:id/guidelines/extract",
            name: "Extract script guidelines",
            description: "Extract text from a guidelines file for persona script generation",
          },
          {
            key: "PERSONA_OPPORTUNITY_SETS_SCRIPTS_CUSTOM_ASSET_DOWNLOAD",
            type: "ACTION",
            method: "GET",
            route: "/persona-opportunity-sets/:id/scripts/:scriptId/custom-asset/download",
            name: "Download persona custom asset",
            description: "Download a custom script asset from a Persona Based set",
          },
          {
            key: "SCRIPTS_BROADCAST",
            type: "ACTION",
            method: "POST",
            route: "/scripts-broadcast",
            name: "Generate scripts for tenants",
            description: "Platform tenants only: adapt the selected scripts into a new set on each chosen client tenant",
          },
        ],
      },
    ],
  },
  {
    id: "personas",
    label: "Personas",
    pagePerm: { key: "PERSONAS_VIEW_PAGE", type: "PAGE", method: "GET", route: "personas", name: "View Personas", description: "Open the Personas page" },
    actionGroups: [
      {
        id: "personas-library",
        label: "Persona library",
        perms: [
          { key: "PERSONAS_LIST",     type: "ACTION", method: "GET",    route: "/personas/list",                  name: "List personas",      description: "See all personas in the library" },
          { key: "PERSONAS_GET",      type: "ACTION", method: "GET",    route: "/personas/:id",                   name: "View persona",       description: "Open a specific persona's details" },
          { key: "PERSONAS_GET_USAGE", type: "ACTION", method: "GET",   route: "/personas/:id/usage",             name: "View persona usage", description: "See which opportunity sets, angles and scripts a persona feeds" },
          { key: "PERSONAS_DISCOVER", type: "ACTION", method: "POST",   route: "/personas/discover",              name: "Discover personas",  description: "Run AI discovery to find new personas" },
          { key: "PERSONAS_REMOVE",   type: "ACTION", method: "DELETE", route: "/personas/:id",                   name: "Remove persona",     description: "Delete a persona from the library" },
          { key: "PERSONAS_REJECT",   type: "ACTION", method: "POST",   route: "/personas/:id/reject",            name: "Reject persona",     description: "Reject a discovered persona with feedback" },
          { key: "PERSONAS_STATUS",   type: "ACTION", method: "GET",    route: "/personas/status/:correlationId", name: "View job status",    description: "Check the status of a running persona job" },
          { key: "PERSONAS_ONBOARDING_STATUS", type: "ACTION", method: "GET", route: "/personas/onboarding/status", name: "Onboarding status", description: "Check persona onboarding progress" },
          { key: "PERSONAS_ONBOARDING_SUGGESTIONS", type: "ACTION", method: "GET", route: "/personas/onboarding/suggestions", name: "Onboarding suggestions", description: "Load BigQuery persona suggestions for onboarding" },
          { key: "PERSONAS_ONBOARDING_ACCEPT", type: "ACTION", method: "POST", route: "/personas/onboarding/accept", name: "Accept onboarding persona", description: "Accept a suggested persona into the library" },
          { key: "PERSONAS_ONBOARDING_REJECT", type: "ACTION", method: "POST", route: "/personas/onboarding/reject", name: "Reject onboarding persona", description: "Reject a suggested persona during onboarding" },
          { key: "PERSONAS_ONBOARDING_UNDO", type: "ACTION", method: "POST", route: "/personas/onboarding/undo", name: "Undo onboarding decision", description: "Reset an accepted or rejected persona back to suggested" },
          { key: "PERSONAS_ONBOARDING_CONTINUE", type: "ACTION", method: "POST", route: "/personas/onboarding/continue", name: "Complete onboarding", description: "Finish persona onboarding and flush draft rejects" },
        ],
      },
    ],
  },
  {
    id: "pre-flight",
    label: "Pre Flight",
    pagePerm: { key: "PREFLIGHT_VIEW_PAGE", type: "PAGE", method: "GET", route: "pre-flight", name: "View Pre Flight", description: "Open the Pre Flight page" },
    actionGroups: [
      {
        id: "pf-view",
        label: "Reports",
        perms: [
          { key: "PREFLIGHT_LIST_REPORTS",  type: "ACTION", method: "GET",    route: "/preflight/reports",           name: "List reports",       description: "See all pre-flight reports" },
          { key: "PREFLIGHT_GET_REPORT",    type: "ACTION", method: "GET",    route: "/preflight/reports/:id",       name: "View report",        description: "Open a specific pre-flight report" },
          { key: "PREFLIGHT_REPORT_STATUS", type: "ACTION", method: "GET",    route: "/preflight/reports/:id/status",name: "View status",        description: "Poll the live analysis status" },
          { key: "PREFLIGHT_GET_CRITERIA",  type: "ACTION", method: "GET",    route: "/preflight/criteria",          name: "View criteria",      description: "Read scoring criteria definitions" },
          { key: "PREFLIGHT_CREATE_REPORT", type: "ACTION", method: "POST",   route: "/preflight/reports",           name: "Create report",      description: "Submit assets for a new pre-flight analysis" },
          { key: "PREFLIGHT_RETRY_REPORT",  type: "ACTION", method: "POST",   route: "/preflight/reports/:id/retry", name: "Retry report",       description: "Re-run a failed or stalled analysis" },
          { key: "PREFLIGHT_RENAME_REPORT", type: "ACTION", method: "PATCH",  route: "/preflight/reports/:id",       name: "Rename / change market", description: "Rename a pre-flight report or reassign its market" },
          { key: "PREFLIGHT_DELETE_REPORT", type: "ACTION", method: "DELETE", route: "/preflight/reports/:id",       name: "Delete report",      description: "Permanently remove a pre-flight report" },
          { key: "PREFLIGHT_FAVORITE_REPORT", type: "ACTION", method: "PATCH", route: "/preflight/reports/:id/favorite", name: "Favorite report", description: "Star a pre-flight report for yourself" },
          { key: "PREFLIGHT_EXPORT_PDF",    type: "ACTION", method: "GET",    route: "/preflight/reports/:id/export.pdf", name: "Export report PDF", description: "Download a pre-flight report as a PDF" },
        ],
      },
      {
        id: "pf-markets",
        label: "Markets",
        perms: [
          { key: "PREFLIGHT_LIST_MARKETS",  type: "ACTION", method: "GET",    route: "/preflight/markets",     name: "List markets",   description: "See the tenant's Pre Flight markets" },
          { key: "PREFLIGHT_GET_INSIGHTS",  type: "ACTION", method: "GET",    route: "/preflight/insights",    name: "View insights",  description: "Read the market-level aggregate dashboard" },
          { key: "PREFLIGHT_GET_CREATIVE_PREVIEWS", type: "ACTION", method: "GET", route: "/preflight/creative-previews", name: "Preview creatives", description: "Load creative thumbnails for the dashboard score rail" },
          { key: "PREFLIGHT_CREATE_MARKET", type: "ACTION", method: "POST",   route: "/preflight/markets",     name: "Add market",     description: "Add a market to the tenant's controlled list" },
          { key: "PREFLIGHT_DELETE_MARKET", type: "ACTION", method: "DELETE", route: "/preflight/markets/:id", name: "Remove market",  description: "Remove an unused market from the tenant's list" },
        ],
      },
      {
        id: "pf-brands",
        label: "Manage brands",
        perms: [
          { key: "PREFLIGHT_CREATE_BRAND", type: "ACTION", method: "POST",   route: "/preflight/brands",     name: "Add brand",    description: "Add a brand name to the tenant's Pre Flight list" },
          { key: "PREFLIGHT_UPDATE_BRAND", type: "ACTION", method: "PATCH",  route: "/preflight/brands/:id", name: "Rename brand", description: "Rename a brand in the tenant's Pre Flight list; reports keep the name they were scored for" },
          { key: "PREFLIGHT_DELETE_BRAND", type: "ACTION", method: "DELETE", route: "/preflight/brands/:id", name: "Remove brand", description: "Remove a brand name from the tenant's Pre Flight list" },
        ],
      },
      {
        id: "pf-uploads",
        label: "Uploads",
        perms: [
          { key: "PREFLIGHT_UPLOAD_PRESIGN",      type: "ACTION", method: "POST", route: "/uploads/presign",       name: "Presign upload",     description: "Request a signed URL to upload an asset" },
          { key: "PREFLIGHT_UPLOAD_CONFIRM",      type: "ACTION", method: "POST", route: "/uploads/confirm",       name: "Confirm upload",     description: "Finalize an upload after the file is in storage" },
          { key: "PREFLIGHT_UPLOAD_STAGE_MEMORY", type: "ACTION", method: "POST", route: "/uploads/stage-memory",  name: "Stage upload (dev)", description: "Store an upload in memory during local development" },
        ],
      },
    ],
  },
  {
    id: "pre-flight-scripts",
    label: "Pre Flight – Scripts",
    pagePerm: { key: "SCRIPT_REVIEW_VIEW_PAGE", type: "PAGE", method: "GET", route: "pre-flight-scripts", name: "View Pre Flight – Scripts", description: "Open the Pre Flight – Scripts page" },
    actionGroups: [
      {
        id: "sr-reviews",
        label: "Reviews",
        perms: [
          { key: "SCRIPT_REVIEW_LIST",         type: "ACTION", method: "GET",    route: "/script-reviews",            name: "List reviews",      description: "See all script reviews" },
          { key: "SCRIPT_REVIEW_GET",          type: "ACTION", method: "GET",    route: "/script-reviews/:id",        name: "View review",       description: "Open a specific review report" },
          { key: "SCRIPT_REVIEW_GET_STATUS",   type: "ACTION", method: "GET",    route: "/script-reviews/:id/status", name: "View status",       description: "Poll the live analysis status" },
          { key: "SCRIPT_REVIEW_CREATE",       type: "ACTION", method: "POST",   route: "/script-reviews",            name: "Create review",     description: "Submit a video + script for a new pre-flight review" },
          { key: "SCRIPT_REVIEW_POST_NOTE",    type: "ACTION", method: "POST",   route: "/script-reviews/:id/notes",  name: "Comment",           description: "Add a collaboration comment on a review" },
          { key: "SCRIPT_REVIEW_CREATE_SHARE", type: "ACTION", method: "POST",   route: "/script-reviews/:id/share",  name: "Create share link", description: "Generate a public guest link for a review" },
          { key: "SCRIPT_REVIEW_REVOKE_SHARE", type: "ACTION", method: "DELETE", route: "/script-reviews/:id/share",  name: "Revoke share link", description: "Revoke a review's public guest link" },
        ],
      },
      {
        id: "sr-uploads",
        label: "Uploads",
        perms: [
          { key: "SCRIPT_REVIEW_UPLOAD_PRESIGN",      type: "ACTION", method: "POST", route: "/uploads/presign",      name: "Presign upload",     description: "Request a signed URL to upload a video or script file" },
          { key: "SCRIPT_REVIEW_UPLOAD_CONFIRM",      type: "ACTION", method: "POST", route: "/uploads/confirm",      name: "Confirm upload",     description: "Finalize an upload after the file is in storage" },
          { key: "SCRIPT_REVIEW_UPLOAD_STAGE_MEMORY", type: "ACTION", method: "POST", route: "/uploads/stage-memory", name: "Stage upload (dev)", description: "Store an upload in memory during local development" },
        ],
      },
    ],
  },
  {
    id: "post-flight",
    label: "Post-Flight",
    pagePerm: {
      key: "POST_FLIGHT_VIEW_PAGE",
      type: "PAGE",
      method: "GET",
      route: "post-flight",
      name: "View Post-Flight",
      description: "Open the Post-Flight page",
    },
    actionGroups: [
      {
        id: "post-flight-list",
        label: "Winning ads",
        perms: [
          {
            key: "POST_FLIGHT_LIST_OPPORTUNITIES",
            type: "ACTION",
            method: "GET",
            route: "/post-flight/opportunities",
            name: "List winning ads",
            description: "Load the qualifying ads for Post-Flight",
          },
          {
            key: "POST_FLIGHT_DOUBLE_DOWN",
            type: "ACTION",
            method: "POST",
            route: "/post-flight/double-down",
            name: "Double down",
            description: "Write scripts from a winning ad",
          },
        ],
      },
    ],
  },
  {
    id: "brand-brain",
    label: "Brand Brain",
    pagePerm: { key: "BRAND_BRAIN_VIEW_PAGE", type: "PAGE", method: "GET", route: "brand-brain", name: "View Brand Brain", description: "Open the Brand Brain page" },
    actionGroups: [
      {
        id: "brand-brain-data",
        label: "Customer knowledge",
        perms: [
          { key: "BRAND_BRAIN_FETCH", type: "ACTION", method: "POST", route: "/brand-brain/fetch", name: "Load Brand Brain", description: "Read the organization's onboarding and enriched brand data" },
          { key: "BRAND_BRAIN_UPDATE", type: "ACTION", method: "POST", route: "/brand-brain/update", name: "Edit Brand Brain", description: "Edit the organization's onboarding fields shown on Brand Brain" },
        ],
      },
    ],
  },
  {
    id: "marketplace",
    label: "Marketplace",
    pagePerm: { key: "MARKETPLACE_VIEW_PAGE", type: "PAGE", method: "GET", route: "marketplace", name: "View Marketplace", description: "Open the Marketplace page" },
    actionGroups: [
      {
        id: "mp-vendors",
        label: "Partners",
        perms: [
          { key: "MARKETPLACE_LIST_VENDORS",      type: "ACTION", method: "GET", route: "/marketplace/vendors",                          name: "List partners",       description: "Browse partners available to connect" },
          { key: "MARKETPLACE_GET_VENDOR_DETAILS", type: "ACTION", method: "GET", route: "/marketplace/vendors/:vendorId/questions",      name: "View partner details", description: "See onboarding questions for a partner" },
        ],
      },
      {
        id: "mp-connections",
        label: "Connections",
        perms: [
          { key: "MARKETPLACE_LIST_CONNECTIONS",           type: "ACTION", method: "GET",    route: "/marketplace/connections",              name: "List connections",    description: "See active partner connections" },
          { key: "MARKETPLACE_CREATE_CONNECTION",        type: "ACTION", method: "POST",   route: "/marketplace/connections",              name: "Create connection",   description: "Start connecting to a partner" },
          { key: "MARKETPLACE_UPDATE_CONNECTION_ANSWERS",  type: "ACTION", method: "PUT",    route: "/marketplace/connections/:id/answers",  name: "Update answers",      description: "Submit or edit onboarding answers" },
          { key: "MARKETPLACE_RESIGN_CONNECTION",          type: "ACTION", method: "POST",   route: "/marketplace/connections/:id/resign",   name: "Re-sign connection",  description: "Re-sign an existing partner connection" },
          { key: "MARKETPLACE_DISCONNECT_VENDOR",          type: "ACTION", method: "DELETE", route: "/marketplace/connections/:id",          name: "Disconnect partner",   description: "Remove an active partner connection" },
        ],
      },
      {
        id: "mp-orders",
        label: "Orders",
        perms: [
          { key: "MARKETPLACE_LIST_ORDERS",        type: "ACTION", method: "GET",  route: "/marketplace/orders",               name: "List orders",        description: "See marketplace orders" },
          { key: "MARKETPLACE_GET_ORDER",          type: "ACTION", method: "GET",  route: "/marketplace/orders/:id",           name: "View order",         description: "Load a marketplace order and its sent partner brief" },
          { key: "MARKETPLACE_LIST_SENT_SCRIPTS",  type: "ACTION", method: "GET",  route: "/marketplace/orders/sent-scripts",  name: "List sent scripts",  description: "See scripts sent via marketplace orders" },
          { key: "MARKETPLACE_GET_ORDER_HANDOFF_DEFAULTS", type: "ACTION", method: "GET", route: "/marketplace/order-handoff-defaults", name: "Read order handoff defaults", description: "Load org handoff defaults when placing an order" },
          { key: "MARKETPLACE_PREVIEW_ORDER_HANDOFF_EMAIL", type: "ACTION", method: "POST", route: "/marketplace/order-handoff-email-preview", name: "Preview partner email", description: "Render the partner handoff email before send" },
          { key: "MARKETPLACE_CREATE_ORDER",       type: "ACTION", method: "POST", route: "/marketplace/orders",                 name: "Create order",       description: "Place a new marketplace order" },
          { key: "MARKETPLACE_RETRY_ORDER",        type: "ACTION", method: "POST", route: "/marketplace/orders/:id/retry",       name: "Retry order",        description: "Re-run a failed marketplace order" },
          { key: "MARKETPLACE_DOWNLOAD_ORDER_SCRIPT_ASSET", type: "ACTION", method: "GET", route: "/marketplace/orders/:id/scripts/:orderScriptId/download", name: "Download order script", description: "Download a script asset from a marketplace order" },
          { key: "MARKETPLACE_LIST_ORDER_SCRIPT_PREFLIGHT_COMMENTS", type: "ACTION", method: "GET", route: "/marketplace/orders/:id/scripts/:orderScriptId/preflight-comments", name: "View order script activity", description: "Read comments and status changes on your order scripts" },
          { key: "MARKETPLACE_ADD_ORDER_SCRIPT_PREFLIGHT_COMMENT", type: "ACTION", method: "POST", route: "/marketplace/orders/:id/scripts/:orderScriptId/preflight-comments", name: "Add order script note", description: "Post a note on your order script that the production partner can see" },
          { key: "MARKETPLACE_SET_ORDER_NOTE_REACTION", type: "ACTION", method: "PUT", route: "/marketplace/orders/:id/scripts/:orderScriptId/preflight-comments/:commentId/reactions", name: "Set order note reaction", description: "Add or remove your reaction on an order script note" },
        ],
      },
    ],
  },
  {
    id: "marketplace-admin",
    label: "Marketplace Admin",
    pagePerm: { key: "MARKETPLACE_ADMIN_VIEW_PAGE", type: "PAGE", method: "GET", route: "marketplace.admin", name: "View Marketplace Admin", description: "Open the Marketplace admin page" },
    actionGroups: [
      {
        id: "mp-admin-catalog",
        label: "Catalog Management",
        perms: [
          { key: "MARKETPLACE_ADMIN_LIST_VENDORS",     type: "ACTION", method: "GET",  route: "/marketplace/admin/vendors",                      name: "List partners",      description: "See all partners in the global catalog" },
          { key: "MARKETPLACE_ADMIN_GET_VENDOR",       type: "ACTION", method: "GET",  route: "/marketplace/admin/vendors/:vendorId",            name: "View partner",       description: "Open a specific partner's details" },
          { key: "MARKETPLACE_ADMIN_CREATE_VENDOR",    type: "ACTION", method: "POST", route: "/marketplace/admin/vendors",                      name: "Create partner",     description: "Add a new partner to the catalog" },
          { key: "MARKETPLACE_ADMIN_REPLACE_VENDOR",   type: "ACTION", method: "PUT",  route: "/marketplace/admin/vendors/:vendorId",            name: "Edit partner",       description: "Update partner profile and settings" },
          { key: "MARKETPLACE_ADMIN_ACTIVATE_VENDOR",  type: "ACTION", method: "POST", route: "/marketplace/admin/vendors/:vendorId/activate",   name: "Activate partner",   description: "Make a partner visible in the marketplace" },
          { key: "MARKETPLACE_ADMIN_DEACTIVATE_VENDOR", type: "ACTION", method: "POST", route: "/marketplace/admin/vendors/:vendorId/deactivate", name: "Deactivate partner", description: "Hide a partner from the marketplace" },
        ],
      },
      {
        id: "mp-admin-contact-emails",
        label: "Partner Contact Emails",
        perms: [
          { key: "MARKETPLACE_ADMIN_APPROVE_CONTACT_EMAIL", type: "ACTION", method: "POST", route: "/marketplace/admin/vendors/:vendorId/contact-emails/:emailId/approve", name: "Approve contact email", description: "Allow order emails to be sent to a partner address (platform admins only)" },
          { key: "MARKETPLACE_ADMIN_REVOKE_CONTACT_EMAIL",  type: "ACTION", method: "POST", route: "/marketplace/admin/vendors/:vendorId/contact-emails/:emailId/revoke",  name: "Revoke contact email",  description: "Stop sending order emails to a partner address (platform admins only)" },
        ],
      },
      {
        id: "mp-admin-team-recipients",
        label: "Team Notifications",
        perms: [
          { key: "MARKETPLACE_ADMIN_LIST_TEAM_RECIPIENTS",  type: "ACTION", method: "GET",    route: "/marketplace/admin/team-recipients",               name: "List team recipients",  description: "See which internal addresses receive order copies and onboarding alerts" },
          { key: "MARKETPLACE_ADMIN_CREATE_TEAM_RECIPIENT", type: "ACTION", method: "POST",   route: "/marketplace/admin/team-recipients",               name: "Add team recipient",    description: "Add an internal address for order copies and/or onboarding alerts" },
          { key: "MARKETPLACE_ADMIN_UPDATE_TEAM_RECIPIENT", type: "ACTION", method: "PUT",    route: "/marketplace/admin/team-recipients/:recipientId",  name: "Edit team recipient",   description: "Rename or pause an internal address" },
          { key: "MARKETPLACE_ADMIN_DELETE_TEAM_RECIPIENT", type: "ACTION", method: "DELETE", route: "/marketplace/admin/team-recipients/:recipientId",  name: "Remove team recipient", description: "Stop copying an internal address on order emails" },
        ],
      },
      {
        id: "mp-admin-slack-notifications",
        label: "Slack Notifications",
        perms: [
          { key: "MARKETPLACE_ADMIN_GET_NOTIFICATION_SETTINGS", type: "ACTION", method: "GET", route: "/marketplace/admin/notification-settings", name: "View Slack notification settings", description: "See which marketplace events post to #marketplace-orders" },
          { key: "MARKETPLACE_ADMIN_UPDATE_NOTIFICATION_SETTINGS", type: "ACTION", method: "PUT", route: "/marketplace/admin/notification-settings", name: "Edit Slack notification settings", description: "Turn individual marketplace Slack alerts on or off" },
        ],
      },
      {
        id: "mp-admin-pending",
        label: "Pending Onboarding",
        perms: [
          { key: "MARKETPLACE_ADMIN_LIST_PENDING_VENDORS", type: "ACTION", method: "GET", route: "/marketplace/admin/vendors/pending", name: "List pending partners", description: "See partners awaiting onboarding from sheet imports" },
          { key: "MARKETPLACE_ADMIN_LINK_PENDING_VENDOR", type: "ACTION", method: "POST", route: "/marketplace/admin/vendors/pending/:pendingId/link", name: "Link pending partner", description: "Map a pending sheet label to an existing catalog partner" },
          { key: "MARKETPLACE_ADMIN_REJECT_PENDING_VENDOR", type: "ACTION", method: "POST", route: "/marketplace/admin/vendors/pending/:pendingId/reject", name: "Cancel partner onboarding", description: "Remove a partner from the pending onboarding queue" },
          { key: "MARKETPLACE_ADMIN_SEND_VENDOR_INVITE", type: "ACTION", method: "POST", route: "/marketplace/admin/vendors/pending/:pendingId/invite", name: "Notify onboarding team", description: "Email the internal onboarding contact that a pending partner label needs marketplace setup" },
          { key: "MARKETPLACE_ADMIN_LIST_PENDING_HANDOFFS", type: "ACTION", method: "GET", route: "/marketplace/admin/orders/pending-handoffs", name: "List pending handoffs", description: "See marketplace order emails waiting to send" },
          { key: "MARKETPLACE_ADMIN_RETRY_ORDER_HANDOFF", type: "ACTION", method: "POST", route: "/marketplace/admin/orders/:id/retry-handoff", name: "Retry order handoff", description: "Re-send a pending marketplace order email" },
        ],
      },
      {
        id: "mp-admin-import",
        label: "Order Import",
        perms: [
          { key: "MARKETPLACE_ADMIN_PARSE_ORDER_IMPORT", type: "ACTION", method: "POST", route: "/marketplace/admin/orders/import/parse", name: "Parse order import", description: "Upload and parse a production spreadsheet" },
          { key: "MARKETPLACE_ADMIN_GET_ORDER_IMPORT", type: "ACTION", method: "GET", route: "/marketplace/admin/orders/import/:importId", name: "View order import", description: "Load import preview and row issues" },
          { key: "MARKETPLACE_ADMIN_UPDATE_ORDER_IMPORT_ROW", type: "ACTION", method: "PATCH", route: "/marketplace/admin/orders/import/:importId/rows/:rowId", name: "Update import row", description: "Edit a single import row mapping" },
          { key: "MARKETPLACE_ADMIN_BULK_ORDER_IMPORT_START_DATES", type: "ACTION", method: "PATCH", route: "/marketplace/admin/orders/import/:importId/client-start-dates", name: "Bulk set start dates", description: "Set client start dates for rows sharing a partner label" },
          { key: "MARKETPLACE_ADMIN_BULK_ORDER_IMPORT_VENDOR_MAPPING", type: "ACTION", method: "PATCH", route: "/marketplace/admin/orders/import/:importId/vendor-mapping", name: "Bulk map partners", description: "Map sheet partner labels to catalog partners" },
          { key: "MARKETPLACE_ADMIN_BULK_ORDER_IMPORT_NEW_VENDOR_MAPPING", type: "ACTION", method: "PATCH", route: "/marketplace/admin/orders/import/:importId/new-vendor-mapping", name: "Bulk map new partners", description: "Mark sheet partner labels as new and capture contact email" },
          { key: "MARKETPLACE_ADMIN_ACCEPT_ORDER_IMPORT_PREFILLED_DATES", type: "ACTION", method: "POST", route: "/marketplace/admin/orders/import/:importId/accept-prefilled-dates", name: "Accept prefilled import dates", description: "Confirm auto-filled client start dates before commit" },
          { key: "MARKETPLACE_ADMIN_ACCEPT_ORDER_IMPORT_WARNINGS", type: "ACTION", method: "POST", route: "/marketplace/admin/orders/import/:importId/accept-warnings", name: "Acknowledge import warnings", description: "Acknowledge non-blocking import warnings before commit" },
          { key: "MARKETPLACE_ADMIN_COMMIT_ORDER_IMPORT", type: "ACTION", method: "POST", route: "/marketplace/admin/orders/import/:importId/commit", name: "Commit order import", description: "Commit validated import rows into opportunity sets and orders" },
          { key: "MARKETPLACE_ADMIN_LIST_PENDING_IMPORT_ORDERS", type: "ACTION", method: "GET", route: "/marketplace/admin/orders/pending-import", name: "List pending import orders", description: "View committed import rows awaiting production handoff" },
          { key: "MARKETPLACE_ADMIN_SEND_IMPORT_ROW_TO_PRODUCTION", type: "ACTION", method: "POST", route: "/marketplace/admin/orders/import-rows/:rowId/send-to-production", name: "Send import row to production", description: "Create a marketplace order from a pending import row" },
        ],
      },
      {
        id: "mp-admin-orders",
        label: "Order Management",
        perms: [
          { key: "MARKETPLACE_UPDATE_ORDER_ETA", type: "ACTION", method: "PUT", route: "/marketplace/orders/:id/eta", name: "Update order ETA", description: "Set, update, or clear a marketplace order delivery ETA (Adge admin)" },
          { key: "MARKETPLACE_UPDATE_ORDER_FULFILLMENT", type: "ACTION", method: "PATCH", route: "/marketplace/orders/:id/fulfillment-status", name: "Update fulfilment", description: "Change the client-visible fulfilment status on an order (Adge admin)" },
          { key: "MARKETPLACE_ADMIN_ENABLE_PARTNER_LOGIN", type: "ACTION", method: "POST", route: "/marketplace/admin/vendors/:vendorId/enable-partner-login", name: "Connect partner org", description: "Create and link a Partner org for a catalog partner that does not have one" },
          { key: "MARKETPLACE_ADMIN_UNLINK_PARTNER_LOGIN", type: "ACTION", method: "POST", route: "/marketplace/admin/vendors/:vendorId/unlink-partner-login", name: "Unlink partner org", description: "Remove the catalog partner's Partner org link without deleting the org or its members" },
        ],
      },
      {
        id: "mp-admin-order-script-activity",
        label: "Order Script Activity",
        perms: [
          { key: "MARKETPLACE_ADMIN_LIST_ORDER_SCRIPT_PREFLIGHT_COMMENTS", type: "ACTION", method: "GET", route: "/marketplace/admin/orders/:id/scripts/:orderScriptId/preflight-comments", name: "View admin order script activity", description: "Read comments and status changes on any client order script" },
          { key: "MARKETPLACE_ADMIN_ADD_ORDER_SCRIPT_PREFLIGHT_COMMENT", type: "ACTION", method: "POST", route: "/marketplace/admin/orders/:id/scripts/:orderScriptId/preflight-comments", name: "Add admin order script note", description: "Post a comment on a client order script" },
          { key: "MARKETPLACE_ADMIN_UPDATE_ORDER_SCRIPT_VIDEO_LABEL", type: "ACTION", method: "PATCH", route: "/marketplace/admin/orders/:id/scripts/:orderScriptId/videos/:deliverableId/label", name: "Rename order script video", description: "Set or clear the display label on a video slot in a client order script" },
          { key: "MARKETPLACE_ADMIN_ADD_ORDER_SCRIPT_VIDEO_SLOT", type: "ACTION", method: "POST", route: "/marketplace/admin/orders/:id/scripts/:orderScriptId/video-slots", name: "Add order script video slot", description: "Allocate another empty video upload slot on a client order script" },
          { key: "MARKETPLACE_ADMIN_DELETE_ORDER_SCRIPT_VIDEO", type: "ACTION", method: "DELETE", route: "/marketplace/admin/orders/:id/scripts/:orderScriptId/videos/:deliverableId", name: "Delete order script video", description: "Remove an extra video from a client order script when another remains" },
        ],
      },
    ],
  },
  {
    id: "marketplace-orders",
    label: "Marketplace Orders",
    pagePerm: { key: "MARKETPLACE_ORDERS_VIEW_PAGE", type: "PAGE", method: "GET", route: "marketplace.orders", name: "View Marketplace Orders", description: "Open the cross-client orders tracker" },
    actionGroups: [
      {
        id: "mp-orders-tracker",
        label: "Orders Tracker",
        perms: [
          { key: "MARKETPLACE_ADMIN_LIST_ALL_ORDERS", type: "ACTION", method: "GET", route: "/marketplace/admin/orders", name: "List all orders", description: "View windowed marketplace orders across clients" },
          { key: "MARKETPLACE_ADMIN_UPDATE_ORDER_SCRIPT_FULFILLMENT", type: "ACTION", method: "PATCH", route: "/marketplace/admin/orders/:id/scripts/:orderScriptId/fulfillment-status", name: "Update script fulfilment", description: "Change per-script fulfilment on any client order" },
          { key: "MARKETPLACE_ADMIN_CREATE_ORDER_FOR_CLIENT", type: "ACTION", method: "POST", route: "/marketplace/admin/orders", name: "Create order for client", description: "Place a marketplace order on behalf of a client org" },
          { key: "MARKETPLACE_ADMIN_DUPLICATE_ORDER_SCRIPT", type: "ACTION", method: "POST", route: "/marketplace/admin/orders/:id/scripts/:orderScriptId/duplicate-script", name: "Duplicate sent script", description: "Copy a sent script into the client's Your Scripts for edits" },
          { key: "MARKETPLACE_ADMIN_SEND_ORDER_SCRIPT_TO_PARTNER", type: "ACTION", method: "POST", route: "/marketplace/admin/orders/:id/scripts/:orderScriptId/send-to-partner", name: "Send script to another partner", description: "Resend a sent script to a different partner as a new order" },
          { key: "MARKETPLACE_UPDATE_ORDER_SCRIPT_FULFILLMENT", type: "ACTION", method: "PATCH", route: "/marketplace/orders/:id/scripts/:orderScriptId/fulfillment-status", name: "Update script fulfilment (scoped)", description: "Change per-script fulfilment when scoped to a client org" },
        ],
      },
    ],
  },
  {
    id: "marketplace-partner",
    label: "Marketplace Partner Portal",
    pagePerm: { key: "MARKETPLACE_PARTNER_VIEW_PAGE", type: "PAGE", method: "GET", route: "marketplace.partner", name: "View Partner Portal", description: "Open the production queue for a partner workspace" },
    actionGroups: [
      {
        id: "mp-partner-orders",
        label: "Partner Orders",
        perms: [
          { key: "SCRIPT_REVIEW_VIEW_PAGE", type: "PAGE", method: "GET", route: "pre-flight-scripts", name: "View Pre Flight – Scripts", description: "Open submitted script reviews in Pre Flight – Scripts" },
          { key: "SCRIPT_REVIEW_GET", type: "ACTION", method: "GET", route: "/script-reviews/:id", name: "View review", description: "Open a linked script review report" },
          { key: "SCRIPT_REVIEW_GET_STATUS", type: "ACTION", method: "GET", route: "/script-reviews/:id/status", name: "View review status", description: "Poll a linked script review while it analyzes" },
          { key: "MARKETPLACE_PARTNER_LIST_ORDERS", type: "ACTION", method: "GET", route: "/marketplace/partner/orders", name: "List partner orders", description: "View assigned production orders" },
          { key: "MARKETPLACE_PARTNER_GET_ORDER", type: "ACTION", method: "GET", route: "/marketplace/partner/orders/:id", name: "View partner order detail", description: "Open an assigned order and load signed storyboard frames" },
          { key: "MARKETPLACE_PARTNER_LIST_ORDER_SCRIPT_PREFLIGHT_COMMENTS", type: "ACTION", method: "GET", route: "/marketplace/partner/orders/:id/scripts/:orderScriptId/preflight-comments", name: "View order script activity", description: "Read comments and status changes on assigned order scripts" },
          { key: "MARKETPLACE_PARTNER_ADD_ORDER_SCRIPT_PREFLIGHT_COMMENT", type: "ACTION", method: "POST", route: "/marketplace/partner/orders/:id/scripts/:orderScriptId/preflight-comments", name: "Add order script note", description: "Post a note on an assigned order script that the client can see" },
          { key: "MARKETPLACE_PARTNER_SET_ORDER_NOTE_REACTION", type: "ACTION", method: "PUT", route: "/marketplace/partner/orders/:id/scripts/:orderScriptId/preflight-comments/:commentId/reactions", name: "Set order note reaction", description: "Add or remove your reaction on an assigned order script note" },
          { key: "MARKETPLACE_PARTNER_UPLOAD_PREFLIGHT", type: "ACTION", method: "POST", route: "/marketplace/partner/orders/:id/scripts/:orderScriptId/preflight", name: "Upload preflight", description: "Submit a video for Pre Flight – Scripts review" },
          { key: "MARKETPLACE_PARTNER_RETRY_PREFLIGHT_REVIEW", type: "ACTION", method: "POST", route: "/marketplace/partner/orders/:id/scripts/:orderScriptId/preflight/retry-review", name: "Retry preflight review publish", description: "Retry publishing a queued Pre Flight – Scripts review after a notify failure" },
          { key: "MARKETPLACE_PARTNER_CHECK_VOICEOVER", type: "ACTION", method: "POST", route: "/marketplace/partner/orders/:id/scripts/:orderScriptId/voiceover-check", name: "Check voiceover against script", description: "Compare an uploaded video's voiceover to the order script before submitting" },
          { key: "MARKETPLACE_PARTNER_DOWNLOAD_ORDER_SCRIPT_ASSET", type: "ACTION", method: "GET", route: "/marketplace/partner/orders/:id/scripts/:orderScriptId/download", name: "Download assigned script", description: "Download a script asset from an assigned production order" },
          { key: "MARKETPLACE_PARTNER_UPDATE_ORDER_SCRIPT_VIDEO_LABEL", type: "ACTION", method: "PATCH", route: "/marketplace/partner/orders/:id/scripts/:orderScriptId/videos/:deliverableId/label", name: "Rename assigned script video", description: "Set or clear the display label on a video slot in an assigned order script" },
          { key: "MARKETPLACE_PARTNER_ADD_ORDER_SCRIPT_VIDEO_SLOT", type: "ACTION", method: "POST", route: "/marketplace/partner/orders/:id/scripts/:orderScriptId/video-slots", name: "Add order script video slot", description: "Allocate another empty video upload slot on an assigned order script" },
          { key: "MARKETPLACE_PARTNER_DELETE_ORDER_SCRIPT_VIDEO_SLOT", type: "ACTION", method: "DELETE", route: "/marketplace/partner/orders/:id/scripts/:orderScriptId/videos/:deliverableId", name: "Remove empty video slot", description: "Delete an unused empty video slot from an assigned order script" },
          { key: "MARKETPLACE_PARTNER_UPSERT_ORDER_SCRIPT_THUMBNAIL", type: "ACTION", method: "PUT", route: "/marketplace/partner/orders/:id/scripts/:orderScriptId/thumbnails", name: "Upload order thumbnail", description: "Attach one thumbnail image to a target-ratio slot on an assigned order script" },
          { key: "MARKETPLACE_PARTNER_DELETE_ORDER_SCRIPT_THUMBNAIL", type: "ACTION", method: "DELETE", route: "/marketplace/partner/orders/:id/scripts/:orderScriptId/thumbnails", name: "Remove order thumbnail", description: "Remove the thumbnail image from a target-ratio slot on an assigned order script" },
        ],
      },
    ],
  },
  {
    id: "overview",
    label: "Overview",
    pagePerm: { key: "OVERVIEW_VIEW_PAGE", type: "PAGE", method: "GET", route: "overview", name: "View Overview", description: "Open the Overview page" },
    actionGroups: [
      {
        id: "overview-data",
        label: "Data",
        perms: [
          { key: "OVERVIEW_INDUSTRIES_LIST", type: "ACTION", method: "GET", route: "/overview/industries", name: "List industries", description: "Load the industry dropdown options" },
          { key: "OVERVIEW_INDUSTRY_PRODUCTS", type: "ACTION", method: "GET", route: "/overview/industry-products", name: "Industry products", description: "Load products for a specific industry" },
        ],
      },
      {
        id: "overview-competitor-defaults",
        label: "Competitor defaults",
        perms: [
          { key: "OVERVIEW_PUT_COMPETITOR_FILTER_DEFAULTS", type: "ACTION", method: "PUT", route: "/overview/competitor-filter-defaults", name: "Save competitor defaults", description: "Save the company's competitor filter defaults from Overview" },
        ],
      },
      {
        id: "overview-shared-presets",
        label: "Shared presets",
        perms: [
          { key: "OVERVIEW_SHARE_FILTER_PRESET", type: "ACTION", method: "POST", route: "/overview/filter-presets/shared", name: "Share Overview preset", description: "Save an Overview filter snapshot for everyone at this company" },
          { key: "OVERVIEW_DELETE_SHARED_FILTER_PRESET", type: "ACTION", method: "DELETE", route: "/overview/filter-presets/shared/:id", name: "Delete shared Overview preset", description: "Remove a company-wide Overview filter snapshot" },
          { key: "OVERVIEW_REORDER_SHARED_FILTER_PRESETS", type: "ACTION", method: "PUT", route: "/overview/filter-presets/shared/order", name: "Reorder shared Overview presets", description: "Change the order of company-wide Overview filter snapshots" },
        ],
      },
    ],
  },
  {
    id: "drilldown",
    label: "Drilldown",
    pagePerm: { key: "DRILLDOWN_VIEW_PAGE", type: "PAGE", method: "GET", route: "drilldown", name: "View Drilldown", description: "Open the Drilldown page" },
    actionGroups: [
      {
        id: "drilldown-data",
        label: "Data",
        perms: [
          { key: "DRILLDOWN_INDUSTRIES_LIST", type: "ACTION", method: "GET", route: "/drilldown/industries", name: "List industries", description: "Load the industry dropdown options" },
          { key: "DRILLDOWN_INDUSTRY_PRODUCTS", type: "ACTION", method: "GET", route: "/drilldown/industry-products", name: "Industry products", description: "Load products for a specific industry" },
        ],
      },
      {
        id: "drilldown-competitor-defaults",
        label: "Competitor defaults",
        perms: [
          { key: "DRILLDOWN_PUT_COMPETITOR_FILTER_DEFAULTS", type: "ACTION", method: "PUT", route: "/drilldown/competitor-filter-defaults", name: "Save competitor defaults", description: "Save the company's competitor filter defaults from Drilldown" },
        ],
      },
      {
        id: "drilldown-shared-presets",
        label: "Shared presets",
        perms: [
          { key: "DRILLDOWN_SHARE_FILTER_PRESET", type: "ACTION", method: "POST", route: "/drilldown/filter-presets/shared", name: "Share Drilldown preset", description: "Save a Drilldown filter snapshot for everyone at this company" },
          { key: "DRILLDOWN_DELETE_SHARED_FILTER_PRESET", type: "ACTION", method: "DELETE", route: "/drilldown/filter-presets/shared/:id", name: "Delete shared Drilldown preset", description: "Remove a company-wide Drilldown filter snapshot" },
          { key: "DRILLDOWN_REORDER_SHARED_FILTER_PRESETS", type: "ACTION", method: "PUT", route: "/drilldown/filter-presets/shared/order", name: "Reorder shared Drilldown presets", description: "Change the order of company-wide Drilldown filter snapshots" },
        ],
      },
    ],
  },
  {
    id: "mcp",
    label: "MCP",
    pagePerm: { key: "MCP_ACCESS", type: "PAGE", method: "GET", route: "mcp", name: "Connect MCP clients", description: "Allow this role to sign in to Nexus from an external chat client over MCP" },
    actionGroups: [
      {
        id: "mcp-tools",
        label: "Tools",
        perms: [
          { key: "MCP_TOOL_GET_RECENT_OPPORTUNITIES", type: "ACTION", method: "POST", route: "/mcp/tools/get_recent_opportunities", name: "Get recent opportunities", description: "Let a chat client read the newest opportunities for a tenant" },
          { key: "MCP_TOOL_LIST_INSIGHT_SLICES", type: "ACTION", method: "POST", route: "/mcp/tools/list_insight_slices", name: "List insight slices", description: "Let a chat client discover which Overview/Dashboard insight slices exist and which filters each one needs" },
          { key: "MCP_TOOL_LIST_FILTER_OPTIONS", type: "ACTION", method: "POST", route: "/mcp/tools/list_filter_options", name: "List insight filter options", description: "Let a chat client read the selectable values for an insight slice's filters — industries, competitor brands, products, and My Brand accounts" },
          { key: "MCP_TOOL_GET_COMPETITIVE_INSIGHTS", type: "ACTION", method: "POST", route: "/mcp/tools/get_competitive_insights", name: "Get competitive insights", description: "Let a chat client fetch Overview/Dashboard competitor analytics — creative-output KPIs, trends, and topic breakdowns from public ad libraries" },
          { key: "MCP_TOOL_GET_BRAND_PERFORMANCE", type: "ACTION", method: "POST", route: "/mcp/tools/get_brand_performance", name: "Get brand performance", description: "Let a chat client fetch Overview/Dashboard own-brand analytics — spend, north-star efficiency, and topic performance from connected ad accounts" },
          { key: "MCP_TOOL_GET_INDUSTRY_OVERVIEW", type: "ACTION", method: "POST", route: "/mcp/tools/get_industry_overview", name: "Get industry overview", description: "Let a chat client read a category snapshot — data vintage plus the industry's brands ranked by 90-day creative volume" },
          { key: "MCP_TOOL_LIST_PERSONAS", type: "ACTION", method: "POST", route: "/mcp/tools/list_personas", name: "List personas", description: "Let a chat client read a tenant's persona library — who each persona is, their pains and motivations, and their spend and ad counts. Approved and awaiting-review personas only; never rejected ones" },
          { key: "MCP_TOOL_LIST_CREATIVE_EXAMPLES", type: "ACTION", method: "POST", route: "/mcp/tools/nexus.list_creative_examples", name: "List creative examples", description: "Let a chat client read the top creative URLs for a report or slideshow. URLs only, at most 25" },
          { key: "MCP_TOOL_SAVE_SCRIPT", type: "ACTION", method: "POST", route: "/mcp/tools/save_script", name: "Save my script", description: "Let a chat client save a script the user already wrote onto a custom opportunity set" },
          { key: "MCP_TOOL_SAVE_BRIEF", type: "ACTION", method: "POST", route: "/mcp/tools/save_brief", name: "Save my brief", description: "Let a chat client save a brief the user already wrote onto a custom opportunity set" },
          { key: "MCP_TOOL_CAPABILITIES_ADMIN_WRITE", type: "ACTION", method: "POST", route: "/mcp/tools/write_feature_description", name: "Write capability descriptions", description: "Let a chat client create or replace the current capability subsection" },
          { key: "MCP_TOOL_CAPABILITIES_ADMIN_READ", type: "ACTION", method: "POST", route: "/mcp/tools/capabilities_admin_read", name: "Read capability descriptions", description: "Let a chat client list capability subsections, read one description, and check permission drift" },
          { key: "MCP_TOOL_GET_MY_CAPABILITIES", type: "ACTION", method: "POST", route: "/mcp/tools/get_my_capabilities", name: "Get my capabilities", description: "Let a chat client describe the Nexus features the user's own page permissions open" },
          { key: "MCP_TOOL_GET_NEXUS_CAPABILITIES", type: "ACTION", method: "POST", route: "/mcp/tools/get_nexus_capabilities", name: "Get Nexus capabilities", description: "Let a chat client describe the Nexus features offered to every customer, whether or not the user has them" },
        ],
      },
    ],
  },
  {
    id: "public-api",
    label: "Public API",
    pagePerm: {
      key: "PUBLIC_API_ACCESS",
      type: "PAGE",
      method: "GET",
      route: "public-api",
      name: "Use Public API",
      description: "Allow programmatic Pre Flight scoring via the Public API (Adge-provisioned service users)",
    },
    actionGroups: [
      {
        id: "public-api-tokens",
        label: "API tokens",
        perms: [
          { key: "PUBLIC_API_LIST_TOKENS", type: "ACTION", method: "GET", route: "/api-tokens", name: "List API tokens", description: "View your Public API tokens" },
          { key: "PUBLIC_API_CREATE_TOKEN", type: "ACTION", method: "POST", route: "/api-tokens", name: "Create API token", description: "Mint a new Public API token" },
          { key: "PUBLIC_API_UPDATE_TOKEN", type: "ACTION", method: "PATCH", route: "/api-tokens/:id", name: "Edit API token", description: "Rename or update token description" },
          { key: "PUBLIC_API_DELETE_TOKEN", type: "ACTION", method: "DELETE", route: "/api-tokens/:id", name: "Revoke API token", description: "Revoke a Public API token" },
        ],
      },
    ],
  },
  {
    id: "prompts",
    label: "Prompts",
    pagePerm: { key: "PROMPTS_VIEW_PAGE", type: "PAGE", method: "GET", route: "prompts", name: "View Prompts", description: "Open the Prompts management page" },
    actionGroups: [
      {
        id: "prompts-browsing",
        label: "Browsing",
        perms: [
          { key: "PROMPTS_LIST",            type: "ACTION", method: "GET",  route: "/prompts",                 name: "List prompts",        description: "See all prompts" },
          { key: "PROMPTS_USAGE",           type: "ACTION", method: "GET",  route: "/prompts/usage",           name: "Prompt usage",        description: "See 7-day LLM usage aggregates per prompt" },
          { key: "PROMPTS_GET",             type: "ACTION", method: "GET",  route: "/prompts/:id",             name: "View prompt",         description: "Open a specific prompt" },
          { key: "PROMPTS_LIST_VERSIONS",   type: "ACTION", method: "GET",  route: "/prompts/:id/versions",    name: "List versions",       description: "See version history of a prompt" },
          { key: "PROMPTS_RESOLVE",         type: "ACTION", method: "GET",  route: "/prompts/by-used-in/:usedIn", name: "Resolve by usage", description: "Look up a prompt by its usage context" },
          { key: "PROMPTS_USED_IN_VALUES",  type: "ACTION", method: "GET",  route: "/prompts/used-in-values",  name: "List usage values",   description: "Load distinct usage values for filters" },
          { key: "PROMPTS_LIST_VARIABLES",  type: "ACTION", method: "GET",  route: "/prompts/variables/:usedIn", name: "List variables",    description: "Load variable names for a usage context" },
          { key: "PROMPTS_LIST_LLM_MODELS", type: "ACTION", method: "GET",  route: "/prompts/llm-models",         name: "List LLM models",    description: "Load the Vertex-synced model catalog for prompts" },
          { key: "PROMPTS_LIST_LABELS", type: "ACTION", method: "GET", route: "/prompts/labels", name: "List labels", description: "Load prompt labels for the filter" },
          { key: "PROMPTS_VARIABLE_USAGES", type: "ACTION", method: "GET", route: "/prompts/variables/:name/usages", name: "Variable usages", description: "See which prompts reference a variable" },
        ],
      },
      {
        id: "prompts-management",
        label: "Management",
        perms: [
          { key: "PROMPTS_CREATE",          type: "ACTION", method: "POST",  route: "/prompts",                     name: "Create prompt",       description: "Create a new prompt" },
          { key: "PROMPTS_UPDATE",          type: "ACTION", method: "PATCH", route: "/prompts/:id",                 name: "Edit prompt",         description: "Update prompt name, status, or content" },
          { key: "PROMPTS_DELETE",          type: "ACTION", method: "DELETE", route: "/prompts/:id",                name: "Delete prompt",       description: "Remove a tenant prompt (kept recoverable in the database)" },
          { key: "PROMPTS_CREATE_VERSION",  type: "ACTION", method: "POST",  route: "/prompts/:id/versions",        name: "Create version",      description: "Save a new version of a prompt" },
          { key: "PROMPTS_REVERT",          type: "ACTION", method: "POST",  route: "/prompts/:id/revert/:versionId", name: "Revert version",   description: "Revert a prompt to a previous version" },
          { key: "PROMPTS_REFRESH_LLM_MODELS", type: "ACTION", method: "POST", route: "/prompts/llm-models/refresh", name: "Refresh LLM models", description: "Bust the cached Vertex model catalog" },
          { key: "PROMPTS_BULK_REPLACE", type: "ACTION", method: "POST", route: "/prompts/bulk-replace", name: "Replace across prompts", description: "Preview and apply a replace across prompts" },
        ],
      },
    ],
  },
  {
    id: "script-delivery",
    label: "Delivery Statistics dashboard",
    pagePerm: {
      key: "SCRIPT_DELIVERY_VIEW_PAGE",
      type: "PAGE",
      method: "GET",
      route: "script-delivery",
      name: "View script delivery",
      description: "Open this month's delivered scripts against client targets",
    },
    actionGroups: [
      {
        id: "script-delivery-summary",
        label: "This month",
        perms: [
          {
            key: "SCRIPT_DELIVERY_GET_SUMMARY",
            type: "ACTION",
            method: "GET",
            route: "/script-delivery/summary",
            name: "View delivery summary",
            description: "See how many scripts each client has delivered this month",
          },
          {
            key: "SCRIPT_DELIVERY_SET_TARGET",
            type: "ACTION",
            method: "PUT",
            route: "/script-delivery/targets/:tenantId",
            name: "Set delivery target",
            description: "Set this month's script target for a client",
          },
        ],
      },
    ],
  },
  {
    id: "order-status-time",
    label: "Order status time",
    pagePerm: {
      key: "ORDER_STATUS_TIME_VIEW_PAGE",
      type: "PAGE",
      method: "GET",
      route: "order-status-time",
      name: "View order status time",
      description: "Open how long orders and scripts stay in each step",
    },
    actionGroups: [
      {
        id: "order-status-time-view",
        label: "View",
        perms: [
          {
            key: "ORDER_STATUS_TIME_LIST",
            type: "ACTION",
            method: "GET",
            route: "/order-status-time",
            name: "List order status time",
            description: "See time spent in each fulfillment status",
          },
        ],
      },
    ],
  },
  {
    id: "ask-nexus",
    label: "Ask Nexus",
    pagePerm: {
      key: "ASK_NEXUS_VIEW_PAGE",
      type: "PAGE",
      method: "GET",
      route: "ask-nexus",
      name: "View Ask Nexus",
      description: "Open the Ask Nexus panel and read your own chats for this organization",
    },
    actionGroups: [
      {
        id: "ask-nexus-chat",
        label: "Chat",
        perms: [
          {
            key: "ASK_NEXUS_SEND_MESSAGE",
            type: "ACTION",
            method: "POST",
            route: "/ask-nexus/conversations",
            name: "Ask a question",
            description: "Ask Nexus about this organization's data, and ask follow-up questions",
          },
          {
            key: "ASK_NEXUS_STOP_ANSWER",
            type: "ACTION",
            method: "POST",
            route: "/ask-nexus/conversations/:conversationId/stop",
            name: "Stop an answer",
            description: "Stop an answer that is still running",
          },
        ],
      },
    ],
  },
  {
    id: "beta-features",
    label: "Beta Features",
    children: [],
  },
];

export function permDefsFromFeatureAreas(areas: FeatureArea[]): PermDef[] {
  return areas.flatMap((area) => [
    ...(area.pagePerm ? [area.pagePerm] : []),
    ...(area.actionGroups ?? []).flatMap((group) => group.perms),
    ...permDefsFromFeatureAreas(area.children ?? []),
  ]);
}

export function featureAreaKeys(area: FeatureArea): string[] {
  return permDefsFromFeatureAreas([area]).map((perm) => perm.key);
}

/**
 * Narrows the catalog to `keys`, dropping areas and action groups left empty.
 * A granter can only pass on what they hold themselves, so restricting the
 * catalog is what makes a group switch read "Full access" over the permissions
 * they can actually see rather than sticking on partial forever.
 */
export function filterFeatureAreasByKeys(
  areas: FeatureArea[],
  keys: ReadonlySet<string>,
): FeatureArea[] {
  const filtered: FeatureArea[] = [];
  for (const area of areas) {
    const pagePerm = area.pagePerm && keys.has(area.pagePerm.key) ? area.pagePerm : undefined;
    const actionGroups = (area.actionGroups ?? [])
      .map((group) => ({ ...group, perms: group.perms.filter((perm) => keys.has(perm.key)) }))
      .filter((group) => group.perms.length > 0);
    const children = filterFeatureAreasByKeys(area.children ?? [], keys);
    if (!pagePerm && actionGroups.length === 0 && children.length === 0) continue;
    filtered.push({ ...area, pagePerm, actionGroups, children });
  }
  return filtered;
}

export const ALL_PERM_DEFS: PermDef[] = permDefsFromFeatureAreas(FEATURE_AREAS);
export const ALL_KEYS = ALL_PERM_DEFS.map((p) => p.key);
export const TOTAL_PERM_COUNT = ALL_KEYS.length;
export const ROLE_PERMISSION_DIALOG_CONTENT_CLASS =
  "flex min-h-0 max-h-[90vh] w-full max-w-[calc(100vw-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-[calc(100vw-2rem)] lg:max-w-[960px]";

// Upload routes are shared across features: tenant-logo, user-avatar,
// marketplace-admin logo, and pre-flight uploads all POST the same `/uploads/*`
// routes. `requirePermission` matches on method+route, so the catalog lists each
// upload route exactly once (under Pre Flight) — sibling upload permission keys
// map to the same routes and intentionally get no duplicate checkbox.

// Drift guard: every SystemPermissions *route* must be represented in the catalog.
// The editor matches granted permissions to catalog entries by method+route, so a
// missing route makes a granted permission invisible and uncountable in the role
// UI — the exact drift this catalog exists to prevent. A pure type-level `keyof`
// check is ineffective here (ALL_PERM_DEFS is typed PermDef[], which widens the
// key union), and enforcing every *key* would demand duplicate upload checkboxes.
// Route coverage is therefore asserted in NewRoleDialog.coverage.test.ts.

// ─── Presets ──────────────────────────────────────────────────────────────────

// Completed Scripts permissions, hoisted so every preset stays in sync when a
// new script permission is added (one edit point instead of four).
const COMPLETED_SCRIPT_PERMISSION_KEYS: SystemPermissionKey[] = [
  "OPPORTUNITY_SETS_SCRIPTS_LIST", "OPPORTUNITY_SETS_SCRIPTS_FACETS",
  "OPPORTUNITY_SETS_SCRIPTS_EXPORT", "OPPORTUNITY_SETS_SCRIPTS_EXPORT_BY_IDS",
  "OPPORTUNITY_SETS_SCRIPTS_REVERT", "OPPORTUNITY_SETS_SCRIPTS_FAVORITE",
  "OPPORTUNITY_SETS_SCRIPTS_REORDER_SCENES", "OPPORTUNITY_SETS_SCRIPTS_AI_EDIT",
  "OPPORTUNITY_SETS_SCRIPTS_SUGGEST", "OPPORTUNITY_SETS_SCRIPTS_RESOLVE_LINKS",
  "OPPORTUNITIES_SCRIPT_STORYBOARD_GENERATE",
];
// Read-only subset for Viewer-style roles (no edit/export-by-ids).
const COMPLETED_SCRIPT_VIEW_KEYS: SystemPermissionKey[] = [
  "OPPORTUNITY_SETS_SCRIPTS_LIST", "OPPORTUNITY_SETS_SCRIPTS_FACETS", "OPPORTUNITY_SETS_SCRIPTS_EXPORT",
];

const OPPORTUNITIES_DATA_PERMISSION_KEYS: SystemPermissionKey[] = [
  "OPPORTUNITIES_INDUSTRIES_LIST",
  "OPPORTUNITIES_INDUSTRY_PRODUCTS",
  "OPPORTUNITIES_TENANT_PRODUCTS",
  "OPPORTUNITIES_TENANT_PRODUCT_TYPES",
  "OPPORTUNITIES_COMPETITOR_OPTIONS",
];

const DASHBOARD_PERMISSION_KEYS: SystemPermissionKey[] = [
  "DASHBOARD_VIEW_PAGE",
  "DASHBOARD_INDUSTRIES_LIST",
  "DASHBOARD_INDUSTRY_PRODUCTS",
];

// The platform Admin panel (cross-org Pub/Sub event history), MCP client access,
// every MCP tool, Post-Flight, and Brand Brain must never be granted in bulk — all are withheld
// even from the "Admin" (full access) preset below and can only be added one role
// at a time via their own checkbox. Mirrors MANUAL_GRANT_ONLY_ROUTES in
// packages/db/src/seed/all-permissions.ts.
// Beta Feature PAGE/ACTION routes are excluded separately by route prefix so
// generated features stay dark without appending keys here.
const MANUAL_GRANT_ONLY_KEYS: SystemPermissionKey[] = [
  "ADMIN_VIEW_PAGE",
  "ADMIN_LIST_EVENT_JOBS",
  "MCP_ACCESS",
  "ADMIN_LIST_CHANGELOG",
  "ADMIN_SYNC_CHANGELOG",
  "ADMIN_REGENERATE_CHANGELOG",
  "ADMIN_EDIT_CHANGELOG",
  "ADMIN_APPROVE_CHANGELOG",
  "ADMIN_REVOKE_CHANGELOG_APPROVAL",
  "ADMIN_SEND_CHANGELOG",
  "ADMIN_LIST_CHANGELOG_RECIPIENTS",
  "ADMIN_ADD_CHANGELOG_RECIPIENT",
  "ADMIN_UPDATE_CHANGELOG_RECIPIENT",
  "ADMIN_REMOVE_CHANGELOG_RECIPIENT",
  "MCP_TOOL_GET_RECENT_OPPORTUNITIES",
  "MCP_TOOL_LIST_INSIGHT_SLICES",
  "MCP_TOOL_LIST_FILTER_OPTIONS",
  "MCP_TOOL_GET_COMPETITIVE_INSIGHTS",
  "MCP_TOOL_GET_BRAND_PERFORMANCE",
  "MCP_TOOL_GET_INDUSTRY_OVERVIEW",
  "MCP_TOOL_LIST_PERSONAS",
  "MCP_TOOL_LIST_CREATIVE_EXAMPLES",
  "MCP_TOOL_SAVE_SCRIPT",
  "MCP_TOOL_SAVE_BRIEF",
  "MCP_TOOL_CAPABILITIES_ADMIN_WRITE",
  "MCP_TOOL_CAPABILITIES_ADMIN_READ",
  "MCP_TOOL_GET_MY_CAPABILITIES",
  "MCP_TOOL_GET_NEXUS_CAPABILITIES",
  "POST_FLIGHT_VIEW_PAGE",
  "POST_FLIGHT_LIST_OPPORTUNITIES",
  "POST_FLIGHT_DOUBLE_DOWN",
  "BRAND_BRAIN_VIEW_PAGE",
  "BRAND_BRAIN_FETCH",
  "BRAND_BRAIN_UPDATE",
];

export function keysForAdminPreset(defs: PermDef[]): SystemPermissionKey[] {
  // Beta Feature routes stay dark: they may be in FEATURE_AREAS (so Identity Hub
  // can grant them) without landing in the Admin bulk preset.
  return defs
    .filter((p) => !MANUAL_GRANT_ONLY_KEYS.includes(p.key) && !isBetaFeaturePermissionRoute(p.route))
    .map((p) => p.key);
}

export const PRESET_DEFS: Array<{ id: string; label: string; description: string; keys: SystemPermissionKey[] }> = [
  {
    id: "Admin",
    label: "Admin",
    description: "Full access to everything",
    keys: keysForAdminPreset(ALL_PERM_DEFS),
  },
  {
    id: "Manager",
    label: "Manager",
    description: "Manage people and content, read-only org settings",
    keys: [
      "IDENTITY_HUB_VIEW_PAGE",
      "IDENTITY_HUB_LIST_USERS", "IDENTITY_HUB_GET_USER_ACCESS", "IDENTITY_HUB_LIST_USERS_ACCESS", "IDENTITY_HUB_CREATE_USER",
      "IDENTITY_HUB_UPDATE_USER", "IDENTITY_HUB_GRANT_ACCESS", "IDENTITY_HUB_REVOKE_ACCESS",
      "IDENTITY_HUB_GENERATE_INVITE",
      "IDENTITY_HUB_LIST_TENANTS", "IDENTITY_HUB_GET_TENANT", "IDENTITY_HUB_UPDATE_TENANT",
      "IDENTITY_HUB_LIST_ACCESS", "IDENTITY_HUB_LIST_ACCESS_BATCH", "IDENTITY_HUB_GET_ACCESS",
      "IDENTITY_HUB_VIEW_AUDIT_LOG",
      "WORKSPACE_VIEW_PAGE",
      ...DASHBOARD_PERMISSION_KEYS,
      "OPPORTUNITIES_VIEW_PAGE",
      ...OPPORTUNITIES_DATA_PERMISSION_KEYS,
      "OPPORTUNITIES_LIST", "OPPORTUNITIES_FACETS", "OPPORTUNITIES_CREATIVES", "OPPORTUNITIES_STATUS",
      "OPPORTUNITIES_GENERATE_NEW_SET", "OPPORTUNITIES_GENERATE", "OPPORTUNITIES_REJECT", "OPPORTUNITIES_EXPORT",
      "OPPORTUNITIES_CANCEL",
      "OPPORTUNITY_SETS_LIST", "OPPORTUNITY_SETS_CREATE", "OPPORTUNITY_SETS_RENAME",
      ...COMPLETED_SCRIPT_PERMISSION_KEYS,
      "PREFLIGHT_VIEW_PAGE",
      "PREFLIGHT_UPLOAD_PRESIGN", "PREFLIGHT_UPLOAD_CONFIRM", "PREFLIGHT_UPLOAD_STAGE_MEMORY",
    ],
  },
  {
    id: "Editor",
    label: "Editor",
    description: "Work with opportunities, view people",
    keys: [
      "IDENTITY_HUB_VIEW_PAGE", "IDENTITY_HUB_LIST_USERS",
      "WORKSPACE_VIEW_PAGE",
      ...DASHBOARD_PERMISSION_KEYS,
      "OPPORTUNITIES_VIEW_PAGE",
      ...OPPORTUNITIES_DATA_PERMISSION_KEYS,
      "OPPORTUNITIES_LIST", "OPPORTUNITIES_FACETS", "OPPORTUNITIES_CREATIVES", "OPPORTUNITIES_STATUS",
      "OPPORTUNITIES_GENERATE", "OPPORTUNITIES_REJECT", "OPPORTUNITIES_EXPORT", "OPPORTUNITIES_CANCEL",
      "OPPORTUNITY_SETS_LIST",
      ...COMPLETED_SCRIPT_PERMISSION_KEYS,
      "PREFLIGHT_VIEW_PAGE",
      "PREFLIGHT_UPLOAD_PRESIGN", "PREFLIGHT_UPLOAD_CONFIRM",
    ],
  },
  {
    id: "Viewer",
    label: "Viewer",
    description: "Read-only access across all sections",
    keys: [
      "IDENTITY_HUB_VIEW_PAGE", "IDENTITY_HUB_LIST_USERS",
      "WORKSPACE_VIEW_PAGE",
      ...DASHBOARD_PERMISSION_KEYS,
      "OPPORTUNITIES_VIEW_PAGE",
      ...OPPORTUNITIES_DATA_PERMISSION_KEYS,
      "OPPORTUNITIES_LIST", "OPPORTUNITIES_FACETS", "OPPORTUNITIES_CREATIVES",
      "OPPORTUNITY_SETS_LIST",
      ...COMPLETED_SCRIPT_VIEW_KEYS,
      "PREFLIGHT_VIEW_PAGE",
    ],
  },
  {
    id: "Adge Customer",
    label: "Adge Customer",
    description: "Full opportunities access without Identity Hub",
    keys: [
      "WORKSPACE_VIEW_PAGE",
      ...DASHBOARD_PERMISSION_KEYS,
      "DASHBOARD_PUT_COMPETITOR_FILTER_DEFAULTS",
      "OPPORTUNITIES_VIEW_PAGE",
      ...OPPORTUNITIES_DATA_PERMISSION_KEYS,
      "OPPORTUNITIES_LIST", "OPPORTUNITIES_FACETS", "OPPORTUNITIES_CREATIVES", "OPPORTUNITIES_STATUS",
      "OPPORTUNITIES_GENERATE_NEW_SET", "OPPORTUNITIES_GENERATE", "OPPORTUNITIES_REJECT", "OPPORTUNITIES_EXPORT",
      "OPPORTUNITIES_CANCEL",
      "OPPORTUNITY_SETS_LIST", "OPPORTUNITY_SETS_CREATE", "OPPORTUNITY_SETS_RENAME",
      "OPPORTUNITY_SETS_DELETE", "OPPORTUNITY_SETS_RETRY",
      ...COMPLETED_SCRIPT_PERMISSION_KEYS,
      "SCRIPT_REVIEW_GET", "SCRIPT_REVIEW_GET_STATUS", "SCRIPT_REVIEW_POST_NOTE",
    ],
  },
];
